import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ClipboardPen, Info } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { useAutoGrow } from "../lib/useAutoGrow";
import { getVoiceStatus } from "../lib/voice";
import DictationControl from "./kit/DictationControl";
import { Separator } from "./ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import LoadFailureNote from "./LoadFailureNote";
import { UnverifiedNote } from "./CoachProfileView";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import api, { formatApiErrorDetail, mediaUrl } from "../lib/api";
import { ABOUT_MAX, HEADLINE_MAX, LEVEL_PLAYED_OPTIONS, SPECIALISM_OPTIONS } from "../lib/coachProfile";
import { resolvePhotoUploadErrorMessage, resolvePhotoRemoveErrorMessage, resolveSaveErrorMessage } from "../lib/settingsErrorMessages";
import { resizePhotoToDataUrl } from "../lib/profilePhoto";
import { initialsFrom } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { TAP_LINK_STYLE } from "./kit/tapTarget";

// Issue #755: HEADLINE_MAX/ABOUT_MAX moved to lib/coachProfile.js so the
// coach set-up screen can reuse the exact same caps instead of retyping
// them — see that module for the DB-CHECK cross-reference.
const SHORT_TEXT_MAX = 600;

/**
 * "My coaching profile" — the coach's own editor, in Settings (issue #290,
 * slice 3). The FIRST coach-specific card on a page whose only role-gated
 * card until now was the player's Coach access card.
 *
 * WHO SEES THIS CARD: every account with `role === "coach"`. #203 — the
 * split of `coach` into team coach and 1:1 coach — is open, and Martin's
 * request was worded for the 1:1 coach, so #290 argued for waiting on
 * #203. Andreas decided (2026-08-18) to ship it to all coaches now. That
 * decision costs nothing to reverse BECAUSE the profile attaches to the
 * user id (`coach_profiles.coach_id`, api/src/db/schema.ts): when #203
 * lands, gating this card on the coach TYPE is a condition on the line in
 * Settings.jsx that renders it, and not a single row moves. Do not
 * re-litigate the gate here — change the condition, not the model.
 *
 * WHAT THE COACH IS ACTUALLY WRITING: claims about themselves that nothing
 * on this platform checks. `UnverifiedNote` is rendered at the top of the
 * form, before the first field, deliberately — the coach reads the exact
 * sentence a parent will read under their claims, at the moment they are
 * deciding what to claim. Showing it only to players would be the easier
 * build and the worse one.
 *
 * WHAT IS NOT ON THIS FORM, and must not be added without a new decision:
 * an email address, a phone number, or a link. Contact stays the
 * coach-links flow. See schema.ts. The photo control at the top is #478's
 * deliberate reversal of the old "no photo" line — Martin's 2026-08-25
 * decision — and it saves through its own route the moment a file is
 * picked, not through the Save button: a photo is one value with its own
 * validation, and holding it hostage to eight text fields would make
 * "did my face upload?" depend on an unrelated form being clean.
 *
 * SAVE MODEL: edit-in-place with an explicit Save, matching the account
 * fields above it (display name) rather than inventing a modal or an
 * autosave. Save is disabled until something actually changed, so the
 * button state is the answer to "did that take?".
 *
 * `frameless` (#456, decision comment 2026-08-27: "one section in
 * settings, both profile and account as one area"): Settings hosts this
 * editor INSIDE its merged "Account & coaching profile" card
 * (AccountProfileCard.jsx), so the host provides the bordered chrome and
 * the section header, and this component drops its own — the h2 block
 * becomes a lead-in sentence, the `<Card>` shell becomes a plain `<div>`.
 * Everything else — the testid, the UnverifiedNote, the photo control and
 * its own save path, every field — is identical in both modes; the same
 * narrow-prop discipline as PlayerProfile.jsx's `showHeading`.
 *
 * `kitType` (issue #976, Settings B, D186.8) — a second, independent prop,
 * default `false`, the `PlayerProfile.jsx`/`AddPlayerCard.jsx` precedent
 * #976's own stop-and-ask named: the former `AccountProfileCardNew` (#1135) is the only
 * caller that sets it (alongside `frameless`), for the coach variant of the
 * redrawn `/settings`; the shipped call site
 * (`AccountProfileCard.jsx:320`'s `<CoachProfileCard frameless />`, pinned
 * verbatim by that file's own test) sets neither, so `false` renders
 * exactly what this file always has — untouched Tailwind sizing, a navy
 * Save button.
 *
 * `true` does two things, both additive over the shipped markup:
 *   1. TYPE RAMP, the same CSS-attribute mechanism `PlayerProfile.jsx` uses
 *      (`index.css`'s own `[data-pp-kit-type="true"]` block): the root
 *      carries `data-cpc-kit-type="true"` ONLY when `kitType` is true (an
 *      empty spread otherwise — never a literal `"false"` attribute, which
 *      would itself be a rendered value), and `index.css` scopes fixed-px
 *      `font-size`/`line-height`/`height` overrides to
 *      this card's own testid paired with `data-cpc-kit-type="true"`
 *      (see `index.css`'s own comment for the exact selector)
 *      — labels, the ten testid'd fields, the avatar-fallback initials and
 *      the two photo-control links. Colour, borders and every other
 *      property these elements carry is untouched.
 *   2. GOLD SAVE (`SettingsCoach.dc.html`'s own header comment, R-003:
 *      "One gold (R-003): Save profile"), replacing the shipped navy — the
 *      one verb this artboard draws gold, the same "the action that
 *      finishes the job" rule D65/D186.7 states for Health's "Save
 *      details" and Passkeys' "Add a passkey". Journey 29's coach-Settings
 *      row counts this one gold element.
 * Specialism chip colour (selected: solid navy/white on the artboard vs the
 * shipped light-navy outline) is deliberately UNCHANGED here — out of this
 * prop's scope; D55/D57/D64/D65 name destructive styling, on-page failures
 * and gold placement, not this one chip's fill, and the reviewer's own
 * D172-item-6 state-evidence pass is where a full pixel audit belongs.
 *
 * #989 round 2 fixes, all scoped to `kitType` (the shipped, non-kitType
 * render is byte-identical):
 *   - F4/D57: profile-save and photo (upload/remove) failures render on
 *     the page via the shared `AlertCallout` shape — `coach-profile-save-
 *     error` ("Save failed", `SettingsCoach.dc.html`'s own `save-failed`
 *     state) and `coach-profile-photo-error` (the existing catch
 *     messages; not drawn with its own id, so this file names one) —
 *     never a toast.
 *   - N2/D55: "Remove photo" moves from a plain underlined text link to
 *     the alert-outlined destructive trigger (`Button`, `ALERT` border/
 *     text), the same shape `PasskeysCard.jsx`'s own Remove takes.
 *   - Screen gap: unselected specialism chips read `#0F172A` (ink) on
 *     white with an `#E2E8F0` border, not the shipped `text-slate-600`
 *     approximation — the border already matched (`border-slate-200`
 *     resolves to `#E2E8F0`), only the text colour moves.
 */
const ALERT = "#B42318";

/** D57's on-page failure shape (round 2 review, S-F1) — kept as its own
 * local copy, matching this codebase's stated preference over a shared
 * import for a ten-line block a handful of files use. */
function AlertCallout({ children, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        padding: "10px 12px",
        background: "#F1F5F9",
        borderLeft: `3px solid ${ALERT}`,
        borderRadius: 10,
        fontSize: 13,
        lineHeight: 1.5,
        color: "#0F172A",
      }}
    >
      <span style={{ display: "inline-flex", flexShrink: 0, marginTop: 1 }}>
        <Info size={16} color={ALERT} />
      </span>
      <span>{children}</span>
    </div>
  );
}

export default function CoachProfileCard({ frameless = false, kitType = false }) {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  // D57 (#989 round 2, F4) — only read/set under kitType; the shipped
  // render still toasts and never touches these.
  const [saveError, setSaveError] = useState("");
  const [photoError, setPhotoError] = useState("");
  const fileRef = useRef(null);
  // #824 — kitType-only auto-grow textareas; unused (ref never attached)
  // on the shipped, non-kitType render.
  const headlineRef = useRef(null);
  const teamsRef = useRef(null);
  // D235 — a coach may dictate the "About you" box. Status is fetched once
  // per card; `DictationControl` renders nothing while voice is unavailable.
  const aboutRef = useRef(null);
  const [voiceStatus, setVoiceStatus] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getVoiceStatus().then((s) => {
      if (!cancelled) setVoiceStatus(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const load = () => {
    setFailed(false);
    return api
      .get("/coach-profiles/me")
      .then((r) => {
        setSaved(r.data);
        setForm(r.data);
      })
      .catch(() => {
        // #271: a swallowed failure must not render as an empty profile —
        // that reads as "we lost what you wrote".
        setFailed(true);
        setForm(null);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useAutoGrow(headlineRef, form?.headline);
  useAutoGrow(teamsRef, form?.teams_coached);

  const toggleSpecialism = (value) => {
    setForm((f) => {
      const current = f.specialisms || [];
      return {
        ...f,
        specialisms: current.includes(value)
          ? current.filter((s) => s !== value)
          : [...current, value],
      };
    });
  };

  // The photo's OWN save path (#478) — POST/DELETE /coach-profiles/me/photo,
  // never PUT /me (whose schema doesn't know the key, so a form save can
  // never clear a photo it didn't mention). `updateUser` pushes the server's
  // stored value into auth context, which is what repaints the topbar
  // avatar immediately — AccountMenu reads `user.profile_photo`, so this is
  // a data change there, not a code change. No reload, no refetch loop.
  const applyPhoto = (photo) => {
    setSaved((sv) => ({ ...sv, photo }));
    setForm((f) => ({ ...f, photo }));
    updateUser({ ...user, profile_photo: photo });
  };

  const changePhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true);
    setPhotoError("");
    try {
      // Client-side resize to a 256px square (WebP, JPEG where the canvas
      // can't encode WebP) BEFORE upload — a phone photo is multi-MB and
      // the API's cap is ~30KB of image. The server re-validates size and
      // content type regardless; this is convenience, not the boundary.
      const photo = await resizePhotoToDataUrl(file);
      const { data } = await api.post("/coach-profiles/me/photo", { photo });
      applyPhoto(data.photo);
      toast.success("Photo updated");
    } catch (e) {
      // D57 (#989 round 2, F4): on the page under kitType, no toast.
      // Shipped leg keeps the toast and its own (unfixed, byte-identical)
      // message computation — `resolvePhotoUploadErrorMessage`'s O1 fix
      // (#989 round 3) is scoped to kitType only, and itself preserves the
      // shipped chain's `e.message` middle tier (see that function's own
      // docstring).
      if (kitType) {
        setPhotoError(resolvePhotoUploadErrorMessage(e));
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Photo upload failed");
      }
    }
    setPhotoBusy(false);
  };

  const removePhoto = async () => {
    setPhotoBusy(true);
    setPhotoError("");
    try {
      const { data } = await api.delete("/coach-profiles/me/photo");
      applyPhoto(data.photo ?? null);
      toast.success("Photo removed");
    } catch (e) {
      // Same O1 scoping as changePhoto's own catch, above.
      if (kitType) {
        setPhotoError(resolvePhotoRemoveErrorMessage(e));
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't remove the photo");
      }
    }
    setPhotoBusy(false);
  };

  const dirty = form && saved && JSON.stringify(normalise(form)) !== JSON.stringify(normalise(saved));

  const save = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const { data } = await api.put("/coach-profiles/me", normalise(form));
      setSaved(data);
      setForm(data);
      toast.success("Coaching profile saved");
    } catch (e) {
      // D57 (#989 round 2, F4): on the page under kitType, drawn words
      // ("Save failed"), no toast. Shipped leg keeps the toast and its
      // own (unfixed, byte-identical) message computation.
      if (kitType) {
        setSaveError(resolveSaveErrorMessage(e));
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
      }
    }
    setSaving(false);
  };

  const body = (
    <>
        {frameless ? (
          /* The host's section header says "coaching profile"; repeating a
             full h2 here would read as a second section inside one card.
             The copy itself survives as the lead-in — it is the sentence
             that tells a coach who reads what they write. */
          <p className="text-sm text-slate-500">
            How you introduce yourself to players. Everything here is optional, and it's shown to
            players you're linked to — including one who's still deciding whether to accept.
          </p>
        ) : (
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#0B2E5915" }}>
              <ClipboardPen size={16} color="#0B2E59" />
            </div>
            <div className="min-w-0">
              <h2 className="font-display font-bold text-lg text-slate-900">My coaching profile</h2>
              <p className="text-sm text-slate-500">
                How you introduce yourself to players. Everything here is optional, and it's shown to
                players you're linked to — including one who's still deciding whether to accept.
              </p>
            </div>
          </div>
        )}

        <UnverifiedNote testId="coach-profile-form-unverified" />

        {failed && <LoadFailureNote what="your coaching profile" onRetry={load} testId="coach-profile-load-failed" />}

        {form && (
          <>
            {/* #478 — the face. Initials while there is no photo; NEVER a
                stock image (the responsive harness asserts no stock host
                anywhere). The photo is small by decision, so the preview
                is honest about the size players will see. */}
            <div className="flex items-center gap-3" data-testid="coach-profile-photo">
              {/* Issue #976 (journey 29 finding): the shipped border here is
                  gold (`border-[#D4A017]`) unconditionally — decorative, not
                  an action, but journey 29's runtime sweep counts ANY
                  gold-computed element, and `SettingsCoach.dc.html:57` draws
                  this circle with a plain slate border (`#CBD5E1`), not
                  gold. Coaches never reached this component through the
                  redrawn `/settings` before Settings B (D186.8), so this is
                  the first time the sweep could see it there — under
                  `kitType`, the border matches the drawing; the shipped,
                  non-kitType render (Settings' own `<CoachProfileCard
                  frameless />`) is unchanged. */}
              <Avatar className={kitType ? "h-14 w-14 shrink-0 border-2" : "h-14 w-14 shrink-0 border-2 border-[#D4A017]"} style={kitType ? { borderColor: "#CBD5E1" } : undefined}>
                {form.photo && <AvatarImage src={mediaUrl(form.photo)} alt="" />}
                <AvatarFallback className="text-sm font-semibold">
                  {initialsFrom(user?.display_name) || "C"}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    data-testid="coach-profile-photo-input"
                    onChange={(e) => {
                      changePhoto(e.target.files?.[0]);
                      // Same file picked twice must fire onChange twice.
                      e.target.value = "";
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className={kitType ? "h-11" : "h-10"}
                    disabled={photoBusy}
                    onClick={() => fileRef.current?.click()}
                    data-testid="coach-profile-photo-change"
                  >
                    {photoBusy ? "Working…" : form.photo ? "Change photo" : "Add photo"}
                  </Button>
                  {form.photo && (kitType ? (
                    // N2 (#989 round 2, D55): the alert-outlined trigger
                    // under kitType, the same shape PasskeysCard.jsx's own
                    // Remove takes. A SEPARATE element from the shipped
                    // link below, not a restyle of it — the shipped
                    // (non-kitType) render is byte-identical.
                    <Button
                      type="button"
                      variant="outline"
                      onClick={removePhoto}
                      disabled={photoBusy}
                      className="h-11"
                      style={{ borderColor: ALERT, color: ALERT }}
                      data-testid="coach-profile-photo-remove"
                    >
                      {photoBusy ? "Removing…" : "Remove photo"}
                    </Button>
                  ) : (
                    <button
                      type="button"
                      onClick={removePhoto}
                      disabled={photoBusy}
                      className="text-xs text-slate-500 hover:text-red-600 underline"
                      data-testid="coach-profile-photo-remove"
                    >
                      Remove photo
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Shown small, next to your profile. Uploads are resized on your device.
                </p>
                {/* D57 (#989 round 2, F4): a photo failure renders here
                    under kitType, never a toast. */}
                {kitType && photoError && <AlertCallout testId="coach-profile-photo-error">{photoError}</AlertCallout>}
              </div>
            </div>

            <div>
              <Label htmlFor="coach-profile-headline">Headline</Label>
              {kitType ? (
                <Textarea
                  id="coach-profile-headline"
                  data-testid="coach-profile-headline"
                  ref={headlineRef}
                  value={form.headline || ""}
                  onChange={(e) => set("headline", e.target.value.replace(/\r?\n/g, " "))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.preventDefault();
                  }}
                  rows={1}
                  maxLength={HEADLINE_MAX}
                  placeholder="e.g. Batting coach, Auckland"
                  // #1014 review, F5 — an `<input>` (this field's
                  // neighbours, e.g. `coach-profile-years`) DOES vertically
                  // centre a single line of text in its own content box,
                  // regardless of how its padding-top/padding-bottom are
                  // declared (verified directly by the review round that
                  // caught this: `coach-profile-years` and this field
                  // rendered with IDENTICAL computed padding — `py-1`,
                  // 4.25px each side — yet still sat 7px apart on screen,
                  // which is only possible if `Input` ignores its own
                  // stated padding split for vertical placement; an
                  // earlier "neither centres, both top-anchor" comment
                  // here was wrong — a same-shape probe on a detached
                  // clone with wildly different sizing gave a misleading
                  // result, not a real measurement of this field). A
                  // `<textarea>` (this one) does NOT auto-centre — it
                  // top-anchors, line 1 starting right after padding-top —
                  // so matching `Input`'s rendered position takes the
                  // padding that makes a single line fill the 44px
                  // content box exactly, centred: (46 − 2px border − 21px
                  // line-height) / 2 = 11.5px, top AND bottom.
                  className="mt-1.5 py-[11.5px]"
                  style={{ minHeight: "var(--nw-size-control)", resize: "none", overflow: "hidden" }}
                  enterKeyHint="done"
                />
              ) : (
                <Input
                  id="coach-profile-headline"
                  data-testid="coach-profile-headline"
                  value={form.headline || ""}
                  onChange={(e) => set("headline", e.target.value)}
                  maxLength={HEADLINE_MAX}
                  placeholder="e.g. Batting coach, Auckland"
                  className="mt-1.5 h-11"
                />
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="coach-profile-years">Years coaching</Label>
                <Input
                  id="coach-profile-years"
                  data-testid="coach-profile-years"
                  type="number"
                  min={0}
                  max={80}
                  value={form.years_coaching ?? ""}
                  onChange={(e) =>
                    set("years_coaching", e.target.value === "" ? null : Number(e.target.value))
                  }
                  className="mt-1.5 h-11"
                />
              </div>
              <div>
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="coach-profile-level">Highest level played</Label>
                  {/* Radix's Select has no empty option (a SelectItem may
                      not carry value=""), so without this a coach who
                      picked the wrong level could never take it back. That
                      is tolerable for `PlayerProfile.jsx`'s batting style;
                      it is not tolerable for a claim about your playing
                      career shown to a player's parents. */}
                  {form.highest_level_played && (
                    <button
                      type="button"
                      onClick={() => set("highest_level_played", null)}
                      className="text-xs text-slate-500 hover:text-slate-800 underline"
                      style={{ ...TAP_LINK_STYLE, justifyContent: "flex-end", "--nw-tap-lh": 1.3333 }}
                      data-testid="coach-profile-level-clear"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <Select
                  value={form.highest_level_played || ""}
                  onValueChange={(v) => set("highest_level_played", v)}
                >
                  {/* D237: the Clear box above is 44px tall and reaches 13.5px below its
                    label row; mt-4 (not mt-1.5) keeps the box off the top of this select. */}
                  <SelectTrigger id="coach-profile-level" data-testid="coach-profile-level" className="mt-4 h-11">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent {...(kitType ? { "data-cpc-kit-type": "true" } : {})}>
                    {LEVEL_PLAYED_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>What you specialise in</Label>
              {/* Toggle chips rather than a multi-select listbox: six fixed
                  options, all of them visible at once, and the tap target
                  is a whole chip on a 390px phone. `aria-pressed` is what
                  makes the selected state readable to a screen reader —
                  the colour alone is not. */}
              <div className="mt-1.5 flex flex-wrap gap-2" data-testid="coach-profile-specialisms">
                {SPECIALISM_OPTIONS.map((o) => {
                  const on = (form.specialisms || []).includes(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleSpecialism(o.value)}
                      data-testid={`coach-profile-specialism-${o.value}`}
                      // #1394 (D221 G4, D354): under kitType the button is a
                      // transparent 44px tap box and the chip is drawn inside
                      // it; the margin pulls the margin box back to the chip's
                      // own `h-10` (rem, so 42.5px at the app's root size),
                      // so the rows keep their pitch. The shipped
                      // (non-kitType) button is byte-identical.
                      className={
                        kitType
                          ? "group inline-flex items-center bg-transparent p-0 outline-none"
                          : `h-10 px-3 rounded-full border-2 text-sm font-semibold transition-colors ${
                              on
                                ? "border-[#0B2E59] bg-[#0B2E59]/5 text-[#0B2E59]"
                                : "border-slate-200 text-slate-600 hover:border-slate-300"
                            }`
                      }
                      style={
                        kitType
                          ? { minHeight: "var(--nw-size-tap)", marginBlock: "calc((2.5rem - var(--nw-size-tap)) / 2)" }
                          : undefined
                      }
                    >
                      {kitType ? (
                        <span
                          data-specialism-chip=""
                          className={`inline-flex h-10 items-center px-3 rounded-full border-2 text-sm font-semibold transition-colors group-focus-visible:ring-2 group-focus-visible:ring-[#0B2E59] ${
                            on ? "border-[#0B2E59] bg-[#0B2E59]/5 text-[#0B2E59]" : "border-slate-200 hover:border-slate-300"
                          }`}
                          // Screen gap (#989 round 2): #0F172A (ink) under
                          // kitType, not the shipped text-slate-600
                          // approximation — the border already matched
                          // (border-slate-200 = #E2E8F0).
                          style={!on ? { color: "#0F172A" } : undefined}
                        >
                          {o.label}
                        </span>
                      ) : (
                        o.label
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="coach-profile-qualifications">Coaching qualifications</Label>
                <Input
                  id="coach-profile-qualifications"
                  data-testid="coach-profile-qualifications"
                  value={form.qualifications || ""}
                  onChange={(e) => set("qualifications", e.target.value)}
                  maxLength={SHORT_TEXT_MAX}
                  placeholder="e.g. NZC Level 2"
                  className="mt-1.5 h-11"
                />
              </div>
              <div>
                <Label htmlFor="coach-profile-region">Region</Label>
                <Input
                  id="coach-profile-region"
                  data-testid="coach-profile-region"
                  value={form.region || ""}
                  onChange={(e) => set("region", e.target.value)}
                  maxLength={120}
                  placeholder="e.g. Auckland"
                  className="mt-1.5 h-11"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="coach-profile-teams">Teams you've coached</Label>
              {kitType ? (
                <Textarea
                  id="coach-profile-teams"
                  data-testid="coach-profile-teams"
                  ref={teamsRef}
                  value={form.teams_coached || ""}
                  onChange={(e) => set("teams_coached", e.target.value.replace(/\r?\n/g, " "))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.preventDefault();
                  }}
                  rows={1}
                  maxLength={SHORT_TEXT_MAX}
                  placeholder="e.g. Driftmoor U17, Corvane 1st XI"
                  // #1014 review, F5 — see the Headline field's own
                  // comment above; the same `py-[11.5px]` override, same
                  // reason.
                  className="mt-1.5 py-[11.5px]"
                  style={{ minHeight: "var(--nw-size-control)", resize: "none", overflow: "hidden" }}
                  enterKeyHint="done"
                />
              ) : (
                <Input
                  id="coach-profile-teams"
                  data-testid="coach-profile-teams"
                  value={form.teams_coached || ""}
                  onChange={(e) => set("teams_coached", e.target.value)}
                  maxLength={SHORT_TEXT_MAX}
                  placeholder="e.g. Driftmoor U17, Corvane 1st XI"
                  className="mt-1.5 h-11"
                />
              )}
            </div>

            <div>
              <Label htmlFor="coach-profile-about">About you</Label>
              <Textarea
                id="coach-profile-about"
                data-testid="coach-profile-about"
                ref={aboutRef}
                value={form.about || ""}
                onChange={(e) => set("about", e.target.value)}
                maxLength={ABOUT_MAX}
                rows={4}
                placeholder="A short introduction — how you work, who you work with."
                className="mt-1.5"
              />
              <DictationControl
                idPrefix="coach-profile-about-mic"
                boxLabel="About you"
                value={form.about || ""}
                onChange={(v) => set("about", v)}
                textareaRef={aboutRef}
                status={voiceStatus}
                maxLength={ABOUT_MAX}
              />
              <p className="text-xs text-slate-400 mt-1">
                {(form.about || "").length}/{ABOUT_MAX}
              </p>
            </div>

            <Separator />

            {/* R-003 (SettingsCoach.dc.html): the one gold action under
                kitType — navy otherwise, unchanged. */}
            <Button
              onClick={save}
              disabled={saving || !dirty}
              className={kitType ? "h-11" : "h-10"}
              style={kitType ? { background: "#D4A017", color: "#0F172A" } : { background: "#0B2E59" }}
              data-testid="coach-profile-save"
            >
              {saving ? "Saving…" : "Save profile"}
            </Button>
            {/* D57 (#989 round 2, F4): a save failure renders here under
                kitType, never a toast. */}
            {kitType && saveError && <AlertCallout testId="coach-profile-save-error">{saveError}</AlertCallout>}
          </>
        )}
    </>
  );

  // The testid lands on whichever element is the card's root, so the
  // journey suite's handle on this card survives the merge unchanged.
  // `data-cpc-kit-type` (kitType only, never a literal "false") is the one
  // attribute index.css's type-ramp rules key off, the same shape
  // PlayerProfile.jsx uses for `data-pp-kit-type`.
  const kitTypeAttr = kitType ? { "data-cpc-kit-type": "true" } : {};

  if (frameless) {
    return (
      <div className="space-y-4" data-testid="settings-coach-profile-card" {...kitTypeAttr}>
        {body}
      </div>
    );
  }

  return (
    <Card className="border-slate-200" data-testid="settings-coach-profile-card" {...kitTypeAttr}>
      <CardContent className="p-6 space-y-4">{body}</CardContent>
    </Card>
  );
}

/**
 * The wire shape, and the comparison basis for "is this dirty".
 *
 * Blank strings go as `""`, which the API collapses to `null` (see
 * `normalise` in routes/coachProfiles.ts) — so clearing a field clears it
 * rather than storing an empty string, and the round-trip returns `null`,
 * which is why the dirty check compares NORMALISED forms on both sides. A
 * raw comparison would call the form dirty forever after one save: `""`
 * typed here never equals the `null` that comes back.
 *
 * Specialisms are sorted so re-selecting the same set in a different order
 * is not a change.
 */
function normalise(f) {
  return {
    headline: f.headline || "",
    years_coaching: f.years_coaching ?? null,
    specialisms: [...(f.specialisms || [])].sort(),
    highest_level_played: f.highest_level_played || null,
    qualifications: f.qualifications || "",
    teams_coached: f.teams_coached || "",
    region: f.region || "",
    about: f.about || "",
  };
}
